import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import {
    useFetchChDurationChartQuery,
    useFetchChLatenessChartQuery,
    useFetchChPerformanceSummaryQuery,
} from "../../Apis/chPerformanceApi";
import {
    buildCompanyChartMap,
    buildCompanySummaryMap,
    mapTimeframeLabelToApi,
} from "../../ChPerformanceReportHelperLib/chPerformanceDataHelper";
import { useTimeframeSelector } from "../../Hooks/useTimeframeSelector";
import { useOnTimeBufferMinutes } from "../../Hooks/useOnTimeBufferMinutes";
import { cx } from "../../Util/classHelpers";
import LoadingSpinner from "../CommonComponents/loadingSpinner";
import ChFilterRow from "../ChAnalytics/ChFilterRow";
import ChPerformanceChartGrid from "./ChPerformanceChartGrid";
import ChPerformanceBreakdownTable from "./ChPerformanceBreakdownTable";
import ChPerformanceDrilldownView from "./ChPerformanceDrilldownView";
import styles from "./chPerformanceReport.module.css";

export const CH_PERFORMANCE_REPORT_COMPONENT = "CH_PERFORMANCE_REPORT_COMPONENT";

const ChPerformanceReportWrapper = () => {
    const { data: enterpriseAccounts } = useFetchCompanyInfosQuery();

    const [selectedLocations, setSelectedLocations] = useState([]);
    const [drilldownState, setDrilldownState] = useState({
        isOpen: false, chartType: null, bucketIndex: null, page: 1,
    });

    const {
        timeframe,
        dateRange,
        startDate,
        endDate,
        onTimeframeChange,
        onRangeChange,
        onRangeClear,
    } = useTimeframeSelector("thisWeek", {
        onReset: useCallback(() => setDrilldownState((s) => ({ ...s, isOpen: false })), []),
    });

    const companyList = useMemo(() => {
        if (!enterpriseAccounts) return [];
        return enterpriseAccounts.getAccountsArray().map((c) => ({
            id: c.companyId,
            name: c.companyName,
        }));
    }, [enterpriseAccounts]);

    const accountsMap = useMemo(
        () => enterpriseAccounts?.getAccountsObj() ?? {},
        [enterpriseAccounts]
    );

    useEffect(() => {
        if (companyList.length > 0 && selectedLocations.length === 0) {
            setSelectedLocations(companyList.map((c) => c.id));
        }
    }, [companyList]); // eslint-disable-line react-hooks/exhaustive-deps

    const companyIdsCsv = selectedLocations.join(",");
    const apiTimeframe = mapTimeframeLabelToApi(timeframe);
    const skip = !companyIdsCsv || (timeframe === "range" && (!startDate || !endDate));
    const rangeParams = { startDate: startDate ?? undefined, endDate: endDate ?? undefined };
    const onTimeBufferMinutes = useOnTimeBufferMinutes();

    const pickup = useFetchChDurationChartQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, type: "PICKUP", ...rangeParams },
        { skip }
    );
    const delivery = useFetchChDurationChartQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, type: "DELIVERY", ...rangeParams },
        { skip }
    );
    const drive = useFetchChDurationChartQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, type: "DRIVE", ...rangeParams },
        { skip }
    );
    const lateness = useFetchChLatenessChartQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, ...rangeParams, onTimeBufferMinutes },
        { skip }
    );
    const summary = useFetchChPerformanceSummaryQuery(
        { companyIds: companyIdsCsv, timeframe: apiTimeframe, ...rangeParams, onTimeBufferMinutes },
        { skip }
    );

    const loading = pickup.isFetching
        || delivery.isFetching
        || drive.isFetching
        || lateness.isFetching
        || summary.isFetching;

    const hasSelection = selectedLocations.length > 0;

    const companyCharts = useMemo(
        () => hasSelection ? buildCompanyChartMap({
            pickup: pickup.data,
            delivery: delivery.data,
            drive: drive.data,
            lateness: lateness.data,
        }) : {},
        [hasSelection, pickup.data, delivery.data, drive.data, lateness.data]
    );

    const companySummaries = useMemo(
        () => hasSelection ? buildCompanySummaryMap(summary.data) : {},
        [hasSelection, summary.data]
    );

    const handleBarClick = useCallback((chartType, bucketIndex) => {
        setDrilldownState({ isOpen: true, chartType, bucketIndex, page: 1 });
    }, []);

    const handleDrilldownClose = useCallback(() => {
        setDrilldownState({ isOpen: false, chartType: null, bucketIndex: null, page: 1 });
    }, []);

    const handleDrilldownPageChange = useCallback((page) => {
        setDrilldownState((s) => ({ ...s, page }));
    }, []);

    return (
        <div className={styles.chRoot}>
            <ChFilterRow
                companyList={companyList}
                selectedLocations={selectedLocations}
                onLocationsChange={setSelectedLocations}
                timeframe={timeframe}
                onTimeframeChange={onTimeframeChange}
                dateRange={dateRange}
                onRangeChange={onRangeChange}
                onRangeClear={onRangeClear}
            />
            {drilldownState.isOpen ? (
                <ChPerformanceDrilldownView
                    chartType={drilldownState.chartType}
                    bucketIndex={drilldownState.bucketIndex}
                    page={drilldownState.page}
                    companyCharts={companyCharts}
                    companySummaries={companySummaries}
                    selectedCompanyIds={selectedLocations}
                    companyIdsCsv={companyIdsCsv}
                    apiTimeframe={apiTimeframe}
                    timeframe={timeframe}
                    startDate={startDate}
                    endDate={endDate}
                    dateRange={dateRange}
                    accountsMap={accountsMap}
                    onBarClick={handleBarClick}
                    onClose={handleDrilldownClose}
                    onPageChange={handleDrilldownPageChange}
                />
            ) : (
                <div className={cx(styles.body, loading && styles.disabled)}>
                    <ChPerformanceChartGrid
                        companyCharts={companyCharts}
                        companySummaries={companySummaries}
                        selectedCompanyIds={selectedLocations}
                        onBarClick={handleBarClick}
                        onTimeBufferMinutes={onTimeBufferMinutes}
                    />
                    <ChPerformanceBreakdownTable
                        companySummaries={companySummaries}
                        accountsMap={accountsMap}
                        selectedCompanyIds={selectedLocations}
                    />
                </div>
            )}
            {loading && !drilldownState.isOpen && (
                <div className={styles.spinnerWrap}>
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};

export default ChPerformanceReportWrapper;
